import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router';
import { ContainerLayout } from 'components/ContainerLayout/ContainerLayout';
import { PageHeader } from 'components/PageHeader/PageHeader';
import { CareProgramsTopicsGrid } from 'components/CareProgramsTopicsGrid/CareProgramsTopicsGrid';
import { CareProgramsList } from 'components/CareProgramsList/CareProgramsList';
import { CareProgramDetailPage } from 'components/CareProgramDetailPage/CareProgramDetailPage';
import { CareProgramSearchResults } from 'components/Resources/CareProgramSearchResults';
import { getHealthTopicRank } from 'features/resources/utils/healthTopicUtils';
import { useLayoutElementsHeight } from 'hooks/useLayoutElementsHeight';
import { useDebouncedValue } from 'hooks/useDebouncedvalue';
import {
  useCareProgramsByTopic,
  useCareProgramRequest,
  useResources,
} from 'features/resources/hooks';
import { ErrorState } from 'components/ErrorState/ErrorState';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import NetworkError from 'assets/images/ErrorState/NetworkError.svg';
import { FeatureFlagKeys } from 'features/DeveloperTools/config/constants';
import { useFeatureFlags } from 'features/DeveloperTools/hooks/useFeatureFlags';
import type { HealthTopic, Program } from 'features/resources/types';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { PendoEventType } from 'features/pendo/types';

const CONTENT_FLEX = 3;
const SEARCH_DEBOUNCE_MS = 300;

const ScrollableContainer = styled(Stack)(() => {
  const { commonContentHeight } = useLayoutElementsHeight();

  return {
    height: commonContentHeight,
    overflow: 'hidden',
    overscrollBehaviorY: 'none',
  };
});

const Content = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flex: 1,
  minHeight: 0,
}));

const ContentPanel = styled(Stack)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flex: CONTENT_FLEX,
    position: 'relative',
    overflowY: 'auto',
    overscrollBehaviorY: 'none',
    backgroundColor: theme.palette.brandBackgroundPrimary.main,
  },
}));

const SearchPanel = styled(Stack)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overscrollBehaviorY: 'none',
    backgroundColor: theme.palette.brandBackgroundPrimary.main,
    paddingBottom: theme.spacing(10),
  },
}));

export const HealthTopicsBrowsePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const isCareProgramSearchEnabled = isEnabled(FeatureFlagKeys.SEARCH_CARE_PROGRAMS);
  const { healthTopics } = useResources();
  const { translation } = useTranslationContext();
  const { trackEvent } = usePendo();

  const {
    selectedProgram,
    programDetail,
    isDetailModalOpen,
    enrollmentStatus,
    handleProgramClick: openProgram,
    handleModalClose,
    error,
    refetch,
  } = useCareProgramRequest();

  const {
    selectedTopic,
    groupedProgramsBySections,
    handleBack,
    handleTopicClick: selectTopic,
  } = useCareProgramsByTopic();
  const handleTopicClick = (healthTopic: HealthTopic) => {
    trackEvent(PendoEventType.SPECIFIC_HEALTH_TOPIC, {
      eventType: PendoEventType.SPECIFIC_HEALTH_TOPIC,
      topicId: healthTopic,
    });
    selectTopic(healthTopic);
  };

  const handleProgramClick = (program: Program) => {
    trackEvent(PendoEventType.SPECIFIC_CARE_PROGRAM, {
      eventType: PendoEventType.SPECIFIC_CARE_PROGRAM,
      programId: String(program.id),
    });
    openProgram(program);
  };

  const topicKeys = healthTopics
    .map((topic) => topic.healthTopic)
    .sort((a, b) => getHealthTopicRank(a) - getHealthTopicRank(b));
  const [searchTerm, setSearchTerm] = useState('');
  const { debouncedValue, flush } = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const isSearching = Boolean(debouncedValue.trim());

  return (
    <ContainerLayout>
      <ScrollableContainer>
        <PageHeader
          showSearch={isCareProgramSearchEnabled}
          isBackVisible
          title={t('care_program_card_title')}
          searchPlaceholder="Search programs"
          onBack={handleBack}
          onSearchIconClick={() => navigate('search')}
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          onSearchSubmit={() => flush()}
        />
        <Content>
          {isSearching ? (
            <SearchPanel>
              <CareProgramSearchResults
                searchTerm={debouncedValue}
                onProgramClick={handleProgramClick}
                selectedProgramId={selectedProgram?.id}
              />
            </SearchPanel>
          ) : (
            <>
              <CareProgramsTopicsGrid
                topics={topicKeys}
                onTopicClick={handleTopicClick}
                selectedTopic={selectedTopic}
              />
              <ContentPanel>
                {error ? (
                  <ErrorState
                    imageArray={[NetworkError]}
                    onRetryClick={refetch}
                    title={translation('no_connection_overlay')}
                    subtitle={translation('error_loading_body')}
                  />
                ) : (
                  <CareProgramsList
                    sections={groupedProgramsBySections}
                    onProgramClick={handleProgramClick}
                    selectedProgramId={selectedProgram?.id}
                  />
                )}
              </ContentPanel>
            </>
          )}
        </Content>

        {programDetail && (
          <CareProgramDetailPage
            program={programDetail}
            open={isDetailModalOpen}
            onClose={handleModalClose}
            enrollmentRequested={
              enrollmentStatus?.hasRequestPending || enrollmentStatus?.isEnrolled
            }
          />
        )}
      </ScrollableContainer>
    </ContainerLayout>
  );
};
